import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnChanges,
  SimpleChanges,
  output,
  signal,
} from '@angular/core';
import { needsReadMoreExcerpt } from '../shared/marketing-read-more.utils';
import type { StoryCardModel } from './success-stories.utils';

@Component({
  selector: 'app-success-stories-story-card',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'cg-ss-story-card-host',
    '[class.is-highlighted]': 'highlighted',
    '[attr.id]': 'cardId || null',
  },
  template: `
    <article class="cg-ss-story-card">
      <button
        type="button"
        class="cg-ss-story-card__media"
        [class.cg-ss-story-card__media--placeholder]="!hasImage()"
        [attr.aria-label]="'Read more about ' + story.name"
        (click)="readMore.emit(story)"
      >
        @if (hasImage()) {
          <img
            [src]="story.image!.cdnUrl"
            [alt]="story.image?.alt ?? story.name"
            loading="lazy"
            (error)="onImageError()"
          />
        } @else {
          <span class="cg-ss-story-card__initials" aria-hidden="true">{{ initials }}</span>
        }
        @if (story.pill) {
          <span class="cg-ss-story-card__badge">{{ story.pill }}</span>
        }
      </button>

      <div class="cg-ss-story-card__body">
        <div class="cg-ss-story-card__quote-wrap">
          <span class="cg-ss-story-card__quote-icon" aria-hidden="true">"</span>
          <blockquote class="cg-ss-story-card__quote">"{{ story.description }}"</blockquote>
          @if (showReadMore) {
            <button
              type="button"
              class="cg-ss-story-card__read-more"
              (click)="readMore.emit(story)"
            >
              Read More →
            </button>
          }
        </div>

        <footer class="cg-ss-story-card__footer">
          <div class="cg-ss-story-card__identity">
            <div class="cg-ss-story-card__name">{{ story.name }}</div>
            @if (story.meta) {
              <div class="cg-ss-story-card__meta">{{ story.meta }}</div>
            }
          </div>
        </footer>
      </div>
    </article>
  `,
})
export class SuccessStoriesStoryCardComponent implements OnChanges {
  @Input({ required: true }) story!: StoryCardModel;
  @Input() highlighted = false;
  @Input() cardId?: string;

  readonly readMore = output<StoryCardModel>();

  private readonly imageLoadFailed = signal(false);

  get showReadMore(): boolean {
    return needsReadMoreExcerpt(this.story.description);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['story']) {
      this.imageLoadFailed.set(false);
    }
  }

  get initials(): string {
    const parts = this.story.name.trim().split(/\s+/).filter(Boolean);
    if (parts.length >= 2) {
      return `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(0)}`.toUpperCase();
    }
    const single = parts[0]?.charAt(0);
    return single ? single.toUpperCase() : '?';
  }

  hasImage(): boolean {
    if (this.imageLoadFailed()) return false;
    return Boolean(this.story.image?.cdnUrl?.trim());
  }

  onImageError(): void {
    this.imageLoadFailed.set(true);
  }
}
